import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Home,
  BookOpen,
  Store,
  MessageCircle,
  Target,
  User,
  Settings,
  Crown,
  LogIn,
  UserPlus,
  Shield,
  Menu,
  X,
  PenTool,
  FolderPlus
} from 'lucide-react'
import { useState } from 'react'
import { useAuthStore } from '../../stores/authStore'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'

const navItems = [
  { name: 'Inicio', href: '/', icon: Home },
  { name: 'Tutoriales', href: '/tutorials', icon: BookOpen },
  { name: 'Marketplace', href: '/marketplace', icon: Store },
  { name: 'Crear', href: '/create', icon: FolderPlus },
  { name: 'Chat', href: '/chat', icon: MessageCircle },
  { name: 'Pizarra', href: '/whiteboard', icon: PenTool },
  { name: 'Misiones', href: '/missions', icon: Target },
]

export default function Navbar() {
  const location = useLocation()
  const [isOpen, setIsOpen] = useState(false)
  const [expandedDesktopItem, setExpandedDesktopItem] = useState<string | null>(null)
  const [expandedMobileItem, setExpandedMobileItem] = useState<string | null>(null)
  const { isAuthenticated, user } = useAuthStore()

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-deep-black/90 backdrop-blur-xl border-b border-gold/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-8 h-8 relative">
              <svg viewBox="0 0 100 100" className="w-full h-full">
                <defs>
                  <linearGradient id="navGoldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#B8A77D" />
                    <stop offset="100%" stopColor="#8A7D5C" />
                  </linearGradient>
                </defs>
                <motion.path
                  d="M50 5 L93.3 27.5 L93.3 72.5 L50 95 L6.7 72.5 L6.7 27.5 Z"
                  fill="none"
                  stroke="url(#navGoldGradient)"
                  strokeWidth="2"
                  className="group-hover:stroke-[#C9B896] transition-colors duration-300"
                />
                <text
                  x="50"
                  y="62"
                  textAnchor="middle"
                  fill="#E8E2D8"
                  fontSize="28"
                  fontFamily="serif"
                  fontWeight="600"
                >
                  B
                </text>
              </svg>
            </div>
            <span className="font-serif text-base tracking-wide text-cream hidden sm:block">
              BEDROCK<span className="text-gold">FORGE</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1 rounded-full border border-white/10 bg-carbon/70 px-2 py-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.href
              const Icon = item.icon
              const showLabel = isActive || expandedDesktopItem === item.name

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onMouseEnter={() => setExpandedDesktopItem(item.name)}
                  onMouseLeave={() => setExpandedDesktopItem(null)}
                  onFocus={() => setExpandedDesktopItem(item.name)}
                  onBlur={() => setExpandedDesktopItem(null)}
                  onClick={() => setExpandedDesktopItem(item.name)}
                  className={`relative px-3 py-1.5 flex items-center text-sm font-medium rounded-full transition-colors duration-200 ${
                    showLabel ? 'gap-2' : 'gap-0'
                  }`}
                >
                  <Icon size={15} className={isActive ? 'text-gold' : 'text-beige/80'} />
                  <AnimatePresence initial={false}>
                    {showLabel && (
                      <motion.span
                        initial={{ width: 0, opacity: 0 }}
                        animate={{ width: 'auto', opacity: 1 }}
                        exit={{ width: 0, opacity: 0 }}
                        transition={{ duration: 0.16, ease: 'easeOut' }}
                        className={`overflow-hidden whitespace-nowrap ${isActive ? 'text-cream' : 'text-beige/70 hover:text-cream'}`}
                      >
                        {item.name}
                      </motion.span>
                    )}
                  </AnimatePresence>
                  {isActive && (
                    <motion.div
                      layoutId="navbar-indicator"
                      className="absolute bottom-0 left-3 right-3 h-0.5 bg-gold rounded-full"
                      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    />
                  )}
                </Link>
              )
            })}
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center gap-2">
            {/* Premium Badge */}
            <Link
              to="/subscriptions"
              className="hidden sm:flex"
            >
              <Badge variant="gold" className="cursor-pointer hover:bg-gold/20 transition-colors text-xs px-3 py-1">
                <Crown size={14} />
                <span>Premium</span>
              </Badge>
            </Link>

            {isAuthenticated ? (
              <>
                {/* Profile */}
                <Link to="/profile">
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-8 h-8 rounded-full bg-anthracite border border-white/10 flex items-center justify-center hover:border-gold/30 transition-colors duration-200"
                  >
                    <User size={15} className="text-beige" />
                  </motion.div>
                </Link>

                {/* Settings */}
                <Link to="/settings">
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-8 h-8 rounded-full bg-anthracite border border-white/10 flex items-center justify-center hover:border-gold/30 transition-colors duration-200"
                  >
                    <Settings size={15} className="text-beige" />
                  </motion.div>
                </Link>

                {/* Admin Panel - Only for admin user */}
                {user?.email === 'santosramonsteven@gmail.com' && (
                  <Link to="/admin">
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="w-8 h-8 rounded-full bg-gold/20 border border-gold/30 flex items-center justify-center hover:border-gold/50 transition-colors duration-200"
                      title="Panel de Admin"
                    >
                      <Shield size={15} className="text-gold" />
                    </motion.div>
                  </Link>
                )}
              </>
            ) : (
              <>
                {/* Login Button */}
                <Link to="/login" className="hidden sm:block">
                  <Button variant="ghost" size="sm" leftIcon={<LogIn size={16} />}>
                    Iniciar Sesión
                  </Button>
                </Link>

                {/* Register Button */}
                <Link to="/register">
                  <Button variant="gold" size="sm" leftIcon={<UserPlus size={16} />}>
                    Registrarse
                  </Button>
                </Link>
              </>
            )}

            {/* Mobile Menu Toggle */}
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsOpen(!isOpen)}
              className="md:hidden w-9 h-9 rounded-full bg-anthracite border border-white/10 flex items-center justify-center"
            >
              <AnimatePresence mode="wait">
                {isOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X size={18} className="text-beige" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Menu size={18} className="text-beige" />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="md:hidden overflow-hidden bg-carbon border-t border-white/5"
          >
            <div className="px-4 py-4 space-y-2">
              {navItems.map((item, index) => {
                const isActive = location.pathname === item.href
                const Icon = item.icon
                const isExpanded = isActive || expandedMobileItem === item.name

                return (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Link
                      to={item.href}
                      onClick={() => {
                        setExpandedMobileItem(item.name)
                        setIsOpen(false)
                      }}
                      className={`flex items-center px-4 py-3 rounded-xl transition-colors duration-200 ${
                        isActive
                          ? 'bg-gold/10 text-gold'
                          : 'text-beige hover:bg-white/5 hover:text-cream'
                      }`}
                    >
                      <Icon size={20} />
                      <AnimatePresence initial={false}>
                        {isExpanded && (
                          <motion.span
                            initial={{ width: 0, opacity: 0 }}
                            animate={{ width: 'auto', opacity: 1 }}
                            exit={{ width: 0, opacity: 0 }}
                            transition={{ duration: 0.16, ease: 'easeOut' }}
                            className="overflow-hidden whitespace-nowrap font-medium ml-3"
                          >
                            {item.name}
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </Link>
                  </motion.div>
                )
              })}

              {/* Mobile Premium Link */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: navItems.length * 0.05 }}
              >
                <Link
                  to="/subscriptions"
                  onClick={() => setIsOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-gold hover:bg-gold/10 transition-colors duration-200"
                >
                  <Crown size={20} />
                  <span className="font-medium">Premium</span>
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  )
}
